import React, { useEffect, useRef } from 'react';
import { Button, Form, Input, Spin, Typography } from 'antd';
import { useMutation } from '@apollo/client';
// Api
import { GET_BRAND_PRODUCT_CATEGORIES } from 'api/brandProductCategory/queries';
import {
  ADD_BRAND_PRODUCT_CATEGORY,
  UPDATE_BRAND_PRODUCT_CATEGORY,
} from 'api/brandProductCategory/mutations';
// Types
import {
  AddBrandProductCategory,
  AddBrandProductCategoryVariables,
} from 'api/brandProductCategory/types/AddBrandProductCategory';
import {
  UpdateBrandProductCategory,
  UpdateBrandProductCategoryVariables,
} from 'api/brandProductCategory/types/UpdateBrandProductCategory';
import { GetBrandProductCategories_getBrandProductCategories } from 'api/brandProductCategory/types/GetBrandProductCategories';
// UI
import { successNotification, errorNotification } from 'ui/Notification';

type FormValues = {
  name: string;
};

export type CreateEditBrandProductCategoryProps = {
  record: GetBrandProductCategories_getBrandProductCategories | null;
  onFinish: () => void;
};

const { Text } = Typography;

const CreateEditBrandProductCategory = ({
  record,
  onFinish,
}: CreateEditBrandProductCategoryProps): JSX.Element => {
  const isEdit = !!record?.id;
  const inputRef = useRef<Input>(null);

  const initialFormValues: FormValues = {
    name: record?.name || '',
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const [addBrandProductCategory, { loading: addLoading }] = useMutation<
    AddBrandProductCategory,
    AddBrandProductCategoryVariables
  >(ADD_BRAND_PRODUCT_CATEGORY, {
    refetchQueries: [{ query: GET_BRAND_PRODUCT_CATEGORIES }],
  });

  const [updateBrandProductCategory, { loading: updateLoading }] = useMutation<
    UpdateBrandProductCategory,
    UpdateBrandProductCategoryVariables
  >(UPDATE_BRAND_PRODUCT_CATEGORY, {
    refetchQueries: [{ query: GET_BRAND_PRODUCT_CATEGORIES }],
  });

  const handleCreate = async (values: FormValues) => {
    try {
      await addBrandProductCategory({
        variables: {
          input: {
            name: values.name.trim(),
          },
        },
      });

      successNotification('Product category created');
      onFinish();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleUpdate = async (values: FormValues) => {
    if (!record?.id) {
      return;
    }

    try {
      await updateBrandProductCategory({
        variables: {
          input: {
            id: record.id,
            name: values.name.trim(),
          },
        },
      });

      successNotification('Product category updated');
      onFinish();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (isEdit) {
      await handleUpdate(values);
    } else {
      await handleCreate(values);
    }
  };

  const loading = addLoading || updateLoading;

  return (
    <Form
      layout="vertical"
      name="createEditBrandProductCategory"
      initialValues={initialFormValues}
      autoComplete="off"
      onFinish={handleSubmit}
    >
      <Form.Item
        name="name"
        label={<Text>Name</Text>}
        rules={[
          {
            required: true,
            message: 'Please enter the category name',
            whitespace: true,
          },
        ]}
      >
        <Input ref={inputRef} placeholder="e.g. Apparel, Beauty, Supplements" />
      </Form.Item>

      {loading ? (
        <Spin size="large" />
      ) : (
        <Button type="primary" htmlType="submit">
          {isEdit ? 'Update' : 'Create'}
        </Button>
      )}
    </Form>
  );
};

export default CreateEditBrandProductCategory;
